import { useEffect, useState } from "react";
import { ImagePlus, LayoutGrid, Lock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/hooks/use-auth";
import { useUpdateProject } from "@/hooks/use-projects";
import { authErrorMessage } from "@/lib/auth-errors";
import type { Project } from "@/lib/api-types";
import { cn } from "@/lib/utils";
import { ProjectCoverImageDialog } from "./project-cover-image-dialog";

/** Backend only accepts these exact values (worker/routes/editor.js PATCH /projects/:id). */
const RETENTION_OPTIONS: { label: string; value: number | null }[] = [
  { label: "Off", value: null },
  { label: "15 days", value: 15 },
  { label: "30 days", value: 30 },
  { label: "60 days", value: 60 },
];

type Props = {
  project: Project;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * All four fields are backed by real endpoints — name/retention via
 * PATCH /projects/:id, cover via migration 0010's presign + PATCH flow, and
 * restricted via migration 0011 (added to the same PATCH body). Reset on open.
 *
 * Restriction flip locked 2026-07-14:
 *   - Flipping ON does NOT populate project_editors. EDIT / EDIT_SHARE users
 *     lose access immediately; they must be re-invited from inside the project.
 *   - Only ADMIN + FULL_ACCESS can toggle (same PATCH endpoint permission).
 *
 * Cover flow (locked 2026-07-13):
 *   - Click the cover box → opens ProjectCoverImageDialog (crop + zoom).
 *   - Sub-dialog Save stages a Blob locally (no R2 call yet).
 *   - Outer Save triggers useUpdateProject with cover: { type: "set", blob }
 *     or { type: "clear" } — presign → PUT → PATCH is orchestrated in the hook.
 */
export function ProjectSettingsDialog({ project, open, onOpenChange }: Props) {
  const { workspaceName } = useAuth();
  const [name, setName] = useState(project.name);
  const [retentionDays, setRetentionDays] = useState<number | null>(project.retentionDays ?? null);
  // Restriction is now backed by projects.restricted (migration 0011). Start from
  // whatever the server reports so re-opening the dialog reflects reality.
  const [isRestricted, setIsRestricted] = useState<boolean>(!!project.restricted);
  const updateProject = useUpdateProject();

  // Cover staging — three states:
  //   coverBlob non-null       → user picked a new cover; upload on Save.
  //   coverRemoved true        → user cleared an existing cover; PATCH null on Save.
  //   both null/false          → no change; keep whatever the project has today.
  const [coverBlob, setCoverBlob] = useState<Blob | null>(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState<string | null>(null);
  const [coverRemoved, setCoverRemoved] = useState(false);
  const [coverDialogOpen, setCoverDialogOpen] = useState(false);

  // Reset every field whenever the dialog opens.
  useEffect(() => {
    if (open) {
      setName(project.name);
      setRetentionDays(project.retentionDays ?? null);
      setIsRestricted(!!project.restricted);
      setCoverBlob(null);
      setCoverPreviewUrl(null);
      setCoverRemoved(false);
    }
  }, [open, project.name, project.retentionDays, project.restricted]);

  // Own the blob: URL for the staged cover preview.
  useEffect(() => {
    return () => {
      if (coverPreviewUrl) URL.revokeObjectURL(coverPreviewUrl);
    };
  }, [coverPreviewUrl]);

  const errorMessage = updateProject.isError ? authErrorMessage(updateProject.error) : null;

  // Which image to show in the little cover box. Priority: staged preview > current server cover > empty.
  const displayedCoverUrl = coverPreviewUrl
    ? coverPreviewUrl
    : coverRemoved
      ? null
      : (project.coverImageUrl ?? null);

  function handleCoverCommit(result: { blob: Blob | null; previewUrl: string | null }) {
    // Revoke any previously staged preview URL.
    if (coverPreviewUrl) URL.revokeObjectURL(coverPreviewUrl);

    if (result.blob) {
      setCoverBlob(result.blob);
      setCoverPreviewUrl(result.previewUrl);
      setCoverRemoved(false);
    } else {
      // Sub-dialog Save with no image = remove. If there was no server cover
      // AND no staged one, this is effectively a no-op.
      setCoverBlob(null);
      setCoverPreviewUrl(null);
      setCoverRemoved(!!project.coverImageUrl);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;

    const payload: Parameters<typeof updateProject.mutate>[0] = {
      id: project.id,
      name: trimmed,
      retentionDays,
    };

    // Only send `restricted` if it changed — keeps the PATCH minimal + avoids
    // triggering side-effect noise on the server when the toggle wasn't touched.
    if (isRestricted !== !!project.restricted) {
      payload.restricted = isRestricted;
    }

    if (coverBlob) {
      payload.cover = { type: "set", blob: coverBlob };
    } else if (coverRemoved) {
      payload.cover = { type: "clear" };
    }

    updateProject.mutate(payload, { onSuccess: () => onOpenChange(false) });
  }

  // Warn only when the CURRENT toggle state would tighten access on Save.
  const willTightenAccess = isRestricted && !project.restricted;

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!updateProject.isPending) onOpenChange(next);
        }}
      >
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <DialogHeader>
              <DialogTitle>Project settings</DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {/* Left: cover photo + name */}
              <div>
                <div className="flex flex-col overflow-hidden rounded-xl border border-border">
                  <button
                    type="button"
                    onClick={() => setCoverDialogOpen(true)}
                    disabled={updateProject.isPending}
                    className="relative grid aspect-video w-full place-items-center bg-secondary/60 transition-colors hover:bg-secondary"
                  >
                    {displayedCoverUrl ? (
                      <img
                        src={displayedCoverUrl}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <ImagePlus className="h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
                    )}
                  </button>
                  <div className="border-t border-border p-2">
                    <Input
                      aria-label="Project name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={updateProject.isPending}
                      className="border-none shadow-none focus-visible:ring-0"
                    />
                  </div>
                </div>
                <p className="mt-1.5 px-0.5 text-[11px] text-muted-foreground">
                  {coverBlob
                    ? "New cover ready — click Save to upload."
                    : coverRemoved
                      ? "Cover will be removed on Save."
                      : "Click the box to change the cover photo."}
                </p>
              </div>

              {/* Right: access, workspace, retention */}
              <div className="flex flex-col gap-5">
                <div>
                  <p className="mb-1.5 text-xs font-medium text-muted-foreground">Access</p>
                  <div className="flex items-start gap-3 rounded-lg border border-border p-3">
                    <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">Make Restricted</p>
                      <p className="text-xs text-muted-foreground">
                        Only people directly invited to the project will have access.
                      </p>
                    </div>
                    <Switch checked={isRestricted} onCheckedChange={setIsRestricted} />
                  </div>
                  {willTightenAccess && (
                    <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                      On Save, Edit &amp; Edit &amp; Share users lose access to this project. Re-invite them from inside the project.
                    </p>
                  )}
                </div>

                <div>
                  <p className="mb-1.5 text-xs font-medium text-muted-foreground">Workspace</p>
                  <div className="flex items-center gap-2 rounded-lg border border-border bg-secondary/40 p-3 text-sm text-muted-foreground">
                    <LayoutGrid className="h-4 w-4" strokeWidth={1.75} />
                    {workspaceName || "Workspace"}
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    A project's workspace can't be changed.
                  </p>
                </div>

                <div>
                  <p className="mb-1.5 text-xs font-medium text-muted-foreground">Retention</p>
                  <div className="flex flex-wrap gap-1.5">
                    {RETENTION_OPTIONS.map((opt) => (
                      <button
                        key={String(opt.value)}
                        type="button"
                        disabled={updateProject.isPending}
                        onClick={() => setRetentionDays(opt.value)}
                        className={cn(
                          "rounded-md border px-3 py-1.5 text-sm transition-colors",
                          retentionDays === opt.value
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border text-muted-foreground hover:text-foreground"
                        )}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={updateProject.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!name.trim() || updateProject.isPending}>
                {updateProject.isPending ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ProjectCoverImageDialog
        open={coverDialogOpen}
        onOpenChange={setCoverDialogOpen}
        // Show either the currently-staged preview or the server cover as the starting image.
        initialImageUrl={displayedCoverUrl}
        onCommit={handleCoverCommit}
      />
    </>
  );
}
